<script setup lang="ts">
import type { Plan } from '@foodlista/shared/plans/types'
import { useMutation } from '@tanstack/vue-query'
import { useCaptureEvent } from '~/domains/analytics/utils/captureEvent'
import { useAuthClient } from '~/domains/auth/auth-client'
import AuthFooter from '~/domains/auth/components/AuthFooter.vue'
import AddressInput from '~/domains/common/components/AddressInput.vue'
import Logo from '~/domains/common/components/Logo.vue'
import { getCoordinatesFromNormalizedParams, getSignupParams, normalizeQueryParams } from '~/domains/restaurants/utils/restaurantParams'

definePageMeta({
  title: 'auth.signupPage.seo.title',
  layout: false,
})

const { t } = useI18n()
const auth = useAuthClient()
const toast = useToast()
const route = useRoute()
const localePath = useLocalePath()
const captureEvent = useCaptureEvent()
const runtimeConfig = useRuntimeConfig()

const normalizedParams = computed(() => normalizeQueryParams(route.query))

function getSelectedPlan(): Plan | undefined {
  const plan = normalizedParams.value.plan

  if (plan === 'BASIC' || plan === 'FREE' || plan === 'PRO') {
    return plan
  }

  return undefined
}

const formState = ref({
  email: '',
  restaurant: {
    name: normalizedParams.value.name ?? undefined,
    address: normalizedParams.value.address ?? '',
    city: normalizedParams.value.city ?? '',
    region: normalizedParams.value.region ?? '',
    country: normalizedParams.value.country ?? '',
    streetNumber: normalizedParams.value.streetNumber ?? '',
    route: normalizedParams.value.route ?? '',
    postalCode: normalizedParams.value.postalCode ?? '',
    coordinates: getCoordinatesFromNormalizedParams(normalizedParams.value),
  },
})
const isAddressError = ref(false)

const { mutate: requestOtp, isPending, isSuccess } = useMutation({
  async mutationFn(data: typeof formState.value) {
    captureEvent('signup_cta_clicked', {
      plan: getSelectedPlan(),
      provider: 'email',
      restaurant: normalizedParams.value.name ?? undefined,
    })

    captureEvent('user_requested_otp', {
      plan: getSelectedPlan(),
      restaurant: normalizedParams.value.name ?? undefined,
    })

    await auth.value.emailOtp.sendVerificationOtp({
      email: data.email,
      type: 'sign-in',
      fetchOptions: {
        async onSuccess() {
          const { coordinates, ...restaurantData } = data.restaurant

          const searchParams = getSignupParams({
            email: data.email,
            restaurant: {
              ...restaurantData,
              coordinates_lat: coordinates?.lat ? coordinates.lat.toString() : undefined,
              coordinates_lng: coordinates?.lng ? coordinates.lng.toString() : undefined,
            },
          }, { signup: 'true' })

          await navigateTo(localePath(`/auth/verify-otp?${searchParams}`))
        },
        throw: true,
      },
    })
  },
  onError() {
    toast.add({
      title: t('auth.signupPage.toasts.error.title'),
      description: t('auth.signupPage.toasts.error.description'),
      color: 'error',
      icon: 'i-tabler-alert-triangle',
    })
  },
})

const { mutate: loginWithGoogle } = useMutation({
  async mutationFn() {
    captureEvent('signup_cta_clicked', {
      plan: getSelectedPlan(),
      provider: 'google',
      restaurant: normalizedParams.value.name ?? undefined,
    })

    captureEvent('user_signed_up', {
      provider: 'google',
      plan: getSelectedPlan(),
      restaurant: normalizedParams.value.name ?? undefined,
    })

    const { coordinates, ...restaurantData } = formState.value.restaurant

    const searchParams = getSignupParams({
      restaurant: {
        ...restaurantData,
        coordinates_lat: coordinates?.lat ? coordinates.lat.toString() : undefined,
        coordinates_lng: coordinates?.lng ? coordinates.lng.toString() : undefined,
      },
    }, { signup: 'true' })

    const { data } = await auth.value.signIn.social({
      provider: 'google',
      callbackURL: `${runtimeConfig.public.siteUrl}${localePath('/dashboard')}?${searchParams}`,
      errorCallbackURL: `${runtimeConfig.public.siteUrl}${localePath('/signup')}?${searchParams}`,
      requestSignUp: true,
      fetchOptions: {
        onError() {
          toast.add({
            color: 'error',
            title: t('auth.login.toasts.error.title'),
            icon: 'i-tabler-alert-triangle',
          })
        },
      },
    })

    if (data?.url) {
      await navigateTo(localePath(data.url))
    }
  },

})
</script>

<template>
  <main
    class="mx-auto flex h-dvh flex-col items-center justify-center gap-10 p-8 dark:bg-gray-900"
  >
    <div class="flex max-w-xs flex-col items-center gap-4">
      <NuxtLinkLocale to="/">
        <Logo class="fill-primary-500 w-40" />
      </NuxtLinkLocale>

      <p class="text-balance text-center text-gray-600 dark:text-gray-400">
        {{ $t("auth.signupPage.subtitle") }}
      </p>
    </div>

    <div v-if="normalizedParams.address" class="flex w-full max-w-xs flex-col gap-10">
      <div class="flex flex-col gap-2">
        <h3 class="mb-2">
          {{ $t("auth.signupPage.business.title") }}
        </h3>

        <UFormField name="name" :label="$t('auth.signupPage.business.name')">
          <UInput v-model="formState.restaurant.name" />
        </UFormField>

        <UFormField name="address" :label="$t('auth.signupPage.business.address')" :error="isAddressError ? $t('auth.signupPage.business.addressError') : undefined">
          <AddressInput v-model="formState.restaurant" v-model:error="isAddressError" :show-map="false" />
        </UFormField>
      </div>

      <USeparator class="max-w-xs" :ui="{ border: 'border-gray-300 dark:border-gray-600' }" />
    </div>

    <div class="flex w-full max-w-xs flex-col gap-4">
      <UButton
        block color="neutral" trailing-icon="i-logos-google-icon"
        @click="() => loginWithGoogle()"
      >
        {{ $t("auth.signupPage.googleSignup") }}
      </UButton>

      <USeparator
        :ui="{
          border: 'border-gray-300 dark:border-gray-800',
        }"
        :label="$t('auth.signupPage.dividerLabel')"
      />

      <UForm
        :state="formState"
        class="flex w-full flex-col gap-4"
        @submit="({ data }) => requestOtp(data)"
      >
        <!-- Honeypot 😈 -->
        <input type="text" name="company" class="hidden">

        <UFormField
          :label="$t('auth.signupPage.fields.email.label')"
          name="email"
        >
          <UInput
            v-model="formState.email"
            type="email"
            required
            placeholder="mario@ristorante.it"
          />
        </UFormField>

        <UButton
          type="submit"
          color="primary"
          size="lg"
          block
          :loading="isPending || isSuccess"
          trailing
          :disabled="normalizedParams.address
            ? !formState.restaurant.coordinates || !formState.restaurant.name || !formState.email
            : !formState.email"
        >
          {{ $t("auth.signupPage.cta.idle") }}
        </UButton>

        <i18n-t
          tag="p"
          keypath="auth.signupPage.signInHint.text"
          scope="global"
          class="text-center text-sm text-gray-600 dark:text-gray-400"
        >
          <template #link>
            <NuxtLink :to="localePath('/login')" class="text-primary-500">
              {{ $t("auth.signupPage.signInHint.link") }}
            </NuxtLink>
          </template>
        </i18n-t>
      </UForm>
    </div>
    <AuthFooter />
  </main>
</template>
